import React, { useEffect, useMemo, useRef } from "react";
import Gantt from "frappe-gantt";
import "./ProductionFrappeGantt.css";

const STATUS_LEGEND = [
  { className: "elite-gantt-scheduled", label: "Scheduled" },
  { className: "elite-gantt-in-production", label: "In Production" },
  { className: "elite-gantt-qc", label: "QC" },
  { className: "elite-gantt-on-hold", label: "On Hold" },
  { className: "elite-gantt-completed", label: "Completed" },
  { className: "elite-gantt-overdue", label: "Overdue" },
  { className: "elite-gantt-cancelled", label: "Cancelled" },
  { className: "elite-gantt-disabled", label: "Disabled" },
];

const STATUS_CLASS_MAP = {
  Scheduled: "elite-gantt-scheduled",
  Planned: "elite-gantt-scheduled",
  InProduction: "elite-gantt-in-production",
  In_Production: "elite-gantt-in-production",
  START: "elite-gantt-in-production",
  RESUME: "elite-gantt-in-production",
  QC: "elite-gantt-qc",
  OnHold: "elite-gantt-on-hold",
  On_Hold: "elite-gantt-on-hold",
  PAUSE: "elite-gantt-on-hold",
  Completed: "elite-gantt-completed",
  COMPLETE: "elite-gantt-completed",
  Cancelled: "elite-gantt-cancelled",
  Canceled: "elite-gantt-cancelled",
  Disabled: "elite-gantt-disabled",
};

const VIEW_MODE_OPTIONS = {
  Day: { columnWidth: 56 },
  Week: { columnWidth: 132 },
  Month: { columnWidth: 126 },
  Year: { columnWidth: 118 },
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const parseLocalDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return isValidDate(date) ? date : null;
};

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeStatus = (status) => {
  if (!status) return "Scheduled";
  return String(status).replace(/\s+/g, "_").replace(/_/g, "");
};

const isDisabledValue = (value) => value === true || value === 1 || value === "1";

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const formatDate = (value) => {
  const date = parseLocalDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getStatusClass = (row, endDate) => {
  const cardData = row.cardData || {};
  const rawStatus = row.metadata?.currentStatus || cardData.status || row.status;
  const normalizedStatus = normalizeStatus(rawStatus);
  const isCompleted = normalizedStatus === "Completed" || normalizedStatus === "COMPLETE";
  const isCancelled = normalizedStatus === "Cancelled" || normalizedStatus === "Canceled";

  if (isDisabledValue(cardData.disabled) || isDisabledValue(row.disabled)) return "elite-gantt-disabled";
  if (!isCompleted && !isCancelled && endDate < startOfToday()) return "elite-gantt-overdue";
  return STATUS_CLASS_MAP[rawStatus] || STATUS_CLASS_MAP[normalizedStatus] || "elite-gantt-scheduled";
};

const buildTaskName = (row) => {
  const cardData = row.cardData || {};
  const orderNumber = displayValue(cardData.orderNumber || row.projectNumber || row.external_key);
  const projectOrProduct =
    displayValue(cardData.projectName || row.projectName) ||
    displayValue(cardData.productName || row.productName) ||
    displayValue(row.name);

  if (orderNumber && projectOrProduct) return `${orderNumber} - ${projectOrProduct}`;
  return orderNumber || projectOrProduct || "Production job";
};

const getProgress = (row) => {
  if (typeof row.progress === "number" && Number.isFinite(row.progress)) {
    return Math.max(0, Math.min(100, row.progress));
  }

  const required = Number(row.cardData?.qtyRequired || row.qtyRequired || 0);
  const done = Number(row.cardData?.qtyDone || row.qty_done || 0);
  if (required > 0 && done >= 0) {
    return Math.max(0, Math.min(100, Math.round((done / required) * 100)));
  }

  return 0;
};

const buildPopup = (row, task, ctx) => {
  const cardData = row.cardData || {};
  const title = ctx.get_title();
  const subtitle = ctx.get_subtitle();
  const details = ctx.get_details();

  title.textContent = buildTaskName(row);
  subtitle.textContent = displayValue(cardData.customer || row.customerName) || "";
  details.textContent = "";

  const detailGrid = document.createElement("div");
  detailGrid.className = "elite-gantt-popup-grid";

  const addDetail = (label, value) => {
    const safeValue = displayValue(value);
    if (!safeValue) return;

    const labelNode = document.createElement("span");
    labelNode.className = "elite-gantt-popup-label";
    labelNode.textContent = label;

    const valueNode = document.createElement("span");
    valueNode.className = "elite-gantt-popup-value";
    valueNode.textContent = safeValue;

    detailGrid.append(labelNode, valueNode);
  };

  addDetail("Order", cardData.orderNumber || row.projectNumber);
  addDetail("Project/Product", cardData.projectName || cardData.productName || row.projectName || row.productName);
  addDetail("Customer", cardData.customer || row.customerName);
  addDetail("Department", cardData.department || row.department);
  addDetail("Machine", cardData.machine || cardData.machineCode || row.machineName || row.machineCode);
  addDetail("Status", row.metadata?.currentStatus || cardData.status || row.status);
  addDetail("Quantity", cardData.qtyRequired || row.qtyRequired);
  addDetail("Scheduled Start", formatDate(cardData.plannedStart || row.plannedStart || task.start));
  addDetail("Scheduled Finish", formatDate(cardData.plannedFinish || row.plannedFinish || task.end));
  addDetail("Hold Reason", cardData.holdReason || row.holdReason);

  details.appendChild(detailGrid);
};

const ProductionFrappeGantt = ({ rows = [], viewMode = "Day", isDark = false, onTaskSelect }) => {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const rowLookupRef = useRef(new Map());

  const { tasks, invalidCount } = useMemo(() => {
    const lookup = new Map();
    let skipped = 0;

    const mappedTasks = rows
      .map((row) => {
        const startDate = parseLocalDate(row.cardData?.plannedStart || row.plannedStart || row.start);
        const endDate = parseLocalDate(row.cardData?.plannedFinish || row.plannedFinish || row.end);

        if (!startDate || !endDate || endDate < startDate) {
          skipped += 1;
          return null;
        }

        const id = String(row.id || row.step_id || row.cardData?.stepid);
        const task = {
          id,
          name: buildTaskName(row),
          start: toLocalDateString(startDate),
          end: toLocalDateString(endDate),
          progress: getProgress(row),
          dependencies: "",
          custom_class: getStatusClass(row, endDate),
        };

        lookup.set(id.replaceAll(" ", "_"), row);
        return task;
      })
      .filter(Boolean);

    rowLookupRef.current = lookup;
    return { tasks: mappedTasks, invalidCount: skipped };
  }, [rows]);

  useEffect(() => {
    let mounted = true;
    const container = containerRef.current;
    if (!container || tasks.length === 0) return undefined;

    container.replaceChildren();

    const options = VIEW_MODE_OPTIONS[viewMode] || VIEW_MODE_OPTIONS.Day;
    ganttRef.current = new Gantt(container, tasks, {
      view_mode: viewMode,
      view_mode_select: false,
      readonly: true,
      readonly_dates: true,
      readonly_progress: true,
      move_dependencies: false,
      today_button: true,
      scroll_to: "today",
      popup_on: "click",
      bar_height: 30,
      bar_corner_radius: 8,
      padding: 18,
      upper_header_height: 46,
      lower_header_height: 34,
      container_height: Math.max(420, Math.min(760, tasks.length * 48 + 130)),
      column_width: options.columnWidth,
      on_click: (task) => {
        if (!mounted || !onTaskSelect) return;
        const row = rowLookupRef.current.get(task.id);
        if (row) onTaskSelect(row);
      },
      popup: (ctx) => {
        const row = rowLookupRef.current.get(ctx.task.id);
        if (row) buildPopup(row, ctx.task, ctx);
      },
    });

    return () => {
      mounted = false;
      ganttRef.current?.hide_popup?.();
      ganttRef.current = null;
      container.replaceChildren();
    };
  }, [tasks, viewMode, onTaskSelect]);

  if (tasks.length === 0) {
    return (
      <div className={`elite-gantt-panel ${isDark ? "elite-gantt-dark" : ""}`}>
        <div className="elite-gantt-empty">
          No scheduled production jobs to display for this selection.
          {invalidCount > 0 && (
            <div>{invalidCount} production jobs are not shown because their scheduled dates are incomplete.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`elite-gantt-panel ${isDark ? "elite-gantt-dark" : ""}`}>
      <div className="elite-gantt-toolbar-note">
        <span>{tasks.length} scheduled jobs</span>
        {invalidCount > 0 && (
          <span>{invalidCount} production jobs are not shown because their scheduled dates are incomplete.</span>
        )}
      </div>

      <div className="elite-gantt-legend" aria-label="Production status legend">
        {STATUS_LEGEND.map((item) => (
          <span className="elite-gantt-legend-item" key={item.className}>
            <span className={`elite-gantt-legend-swatch ${item.className}`} />
            {item.label}
          </span>
        ))}
      </div>

      <div className="elite-gantt-chart-shell">
        <div ref={containerRef} />
      </div>
    </div>
  );
};

export default ProductionFrappeGantt;
